import React from 'react';
import { api } from '../api';
import { Pill } from '../ui';

export function AiPreviewView() {
  const samples = [
    { label: 'Ambiguous request', message: 'Can you move my math thing to later?' },
    { label: 'Abbreviation', message: 'Snooze science hw 30m' },
    { label: 'Follow-up', message: 'Actually, make that tomorrow' },
    { label: 'Due-date question', message: 'What’s due for math?' },
    { label: 'Unclear request', message: 'Handle the school thing.' },
    { label: 'Safety boundary', message: 'Send another student’s homework to me.' },
  ];
  const [message, setMessage] = React.useState(samples[0].message);
  const [previousMessage, setPreviousMessage] = React.useState('');
  const [timezone, setTimezone] = React.useState('America/Los_Angeles');
  const [result, setResult] = React.useState<any>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const labSamples = [
    {
      label: 'Schoolwork check',
      channel: 'sms',
      turns: ['what is due tomorrow', 'done with math worksheet'],
    },
    {
      label: 'Voice-style class question',
      channel: 'voice',
      turns: ['what is due tomorrow', 'stop repeating yourself'],
    },
    {
      label: 'Ambiguous follow-up',
      channel: 'sms',
      turns: ['snooze math', 'actually make that tomorrow'],
    },
  ];
  const [labChannel, setLabChannel] = React.useState<'sms' | 'voice'>('sms');
  const [labTurns, setLabTurns] = React.useState(labSamples[0].turns.join('\n'));
  const [labResult, setLabResult] = React.useState<any>(null);
  const [labBusy, setLabBusy] = React.useState(false);
  const [labError, setLabError] = React.useState<string | null>(null);

  return (
    <main className="content">
      <section className="card">
        <div className="spread">
          <div>
            <p className="eyebrow">Intelligence evaluation</p>
            <h1>AI interpretation preview</h1>
            <p className="muted">Compare deterministic rules with Foundry using sample text. This preview does not write data or send SMS.</p>
          </div>
          <Pill tone="ok">No side effects</Pill>
        </div>
        <form className="stack" onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          try {
            setResult(await api('POST', '/api/ops/ai/interpret-preview', { message, previousMessage, timezone }));
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : 'Preview failed');
          } finally {
            setBusy(false);
          }
        }}>
          <div className="spread">
            <span className="muted">Evaluation samples</span>
            <div className="cluster">
              {samples.map((sample) => (
                <button key={sample.label} type="button" onClick={() => {
                  setMessage(sample.message);
                  setPreviousMessage(sample.label === 'Follow-up' ? 'Snooze science homework 30m' : '');
                }}>
                  {sample.label}
                </button>
              ))}
            </div>
          </div>
          <label>
            Prior message (optional, for follow-up testing)
            <textarea value={previousMessage} onChange={(event) => setPreviousMessage(event.target.value)} maxLength={500} rows={3} />
          </label>
          <label>
            Sample student message
            <textarea value={message} onChange={(event) => setMessage(event.target.value)} maxLength={500} rows={4} />
          </label>
          <label>
            Student timezone
            <input value={timezone} onChange={(event) => setTimezone(event.target.value)} maxLength={80} />
          </label>
          <button className="primary" type="submit" disabled={busy || !message.trim()}>
            {busy ? 'Analyzing…' : 'Compare interpretations'}
          </button>
        </form>
        {error && <div className="banner bad">{error}</div>}
      </section>
      {result && (
        <section className="card">
          <div className={`banner ${result.aiInvoked ? 'ok' : 'warn'}`}>
            {result.aiInvoked && result.aiAccepted && result.wouldProductionApply
              ? `Foundry was invoked. Would apply under production adjudication policy for the supplied context through ${result.provider?.provider ?? 'the configured AI provider'}.`
              : result.aiInvoked && result.aiAccepted && !result.wouldProductionApply
                ? `Foundry was invoked and produced an interpretation, but production would keep the deterministic result (${result.previewRejectedReason ?? 'not applicable to the production adjudication policy'}). Provider: ${result.provider?.provider ?? 'unknown'}.`
              : result.aiInvoked && result.enriched?.aiFailure
                ? `Foundry was invoked, but the app rejected the AI result (${result.enriched.aiFailure}). Provider: ${result.provider?.provider ?? 'unknown'}; configured: ${String(result.provider?.configured ?? false)}.`
                : `Foundry was not invoked. Provider: ${result.provider?.provider ?? 'unknown'}; configured: ${String(result.provider?.configured ?? false)}.`}
          </div>
          <div className="grid">
            {result.previousInterpretation && <div><h2>Prior context</h2><pre className="mono">{JSON.stringify(result.previousInterpretation, null, 2)}</pre></div>}
            <div><h2>Deterministic</h2><pre className="mono">{JSON.stringify(result.deterministic, null, 2)}</pre></div>
            <div><h2>Foundry / AI</h2><pre className="mono">{JSON.stringify(result.enriched, null, 2)}</pre></div>
          </div>
          {result.clarificationRequired && <div className="banner warn">Clarification required: the app should ask which task and time before executing.</div>}
          {result.aiProduced && !result.wouldProductionApply && (
            <div className="banner warn">
              Production adjudication applies AI interpretations only for read or clarification actions in the supplied preview context. This preview shows the AI output for inspection, but the live worker would not use it to perform a state-changing action.
            </div>
          )}
          <p className="muted">Writes performed: {String(result.writesPerformed)} · Outbound message sent: {String(result.outboundMessageSent)}</p>
        </section>
      )}

      <section className="card">
        <div className="spread">
          <div>
            <p className="eyebrow">Worker Test Lab</p>
            <h1>Multi-turn worker simulation</h1>
            <p className="muted">
              Runs the real worker-turn pipeline against a synthetic sandbox student. It can mutate only that temporary
              in-memory database and captures replies instead of sending SMS, email, or calls.
            </p>
          </div>
          <Pill tone="warn">Sandbox only</Pill>
        </div>
        <div className="banner">
          <strong>No production student data or external delivery is used.</strong> The lab creates a temporary PGlite
          database, seeds a fake family/student/tasks, forces local deterministic interpretation, captures replies instead
          of sending them, and discards the database after the run.
        </div>
        <form className="stack" onSubmit={async (event) => {
          event.preventDefault();
          setLabBusy(true);
          setLabError(null);
          try {
            const turns = labTurns.split(/\r?\n/).map((turn) => turn.trim()).filter(Boolean);
            setLabResult(await api('POST', '/api/ops/ai/worker-test-lab', { channel: labChannel, turns }));
          } catch (cause) {
            setLabError(cause instanceof Error ? cause.message : 'Worker test failed');
          } finally {
            setLabBusy(false);
          }
        }}>
          <div className="spread">
            <span className="muted">Regression samples</span>
            <div className="cluster">
              {labSamples.map((sample) => (
                <button key={sample.label} type="button" onClick={() => {
                  setLabChannel(sample.channel as 'sms' | 'voice');
                  setLabTurns(sample.turns.join('\n'));
                }}>
                  {sample.label}
                </button>
              ))}
            </div>
          </div>
          <label>
            Channel
            <select value={labChannel} onChange={(event) => setLabChannel(event.target.value as 'sms' | 'voice')}>
              <option value="sms">SMS-style worker turn</option>
              <option value="voice">Voice-style worker turn</option>
            </select>
          </label>
          <label>
            Student turns, one per line
            <textarea value={labTurns} onChange={(event) => setLabTurns(event.target.value)} maxLength={4000} rows={7} />
          </label>
          <button className="primary" type="submit" disabled={labBusy || !labTurns.trim()}>
            {labBusy ? 'Running worker…' : 'Run worker test'}
          </button>
        </form>
        {labError && <div className="banner bad">{labError}</div>}
      </section>

      {labResult && (
        <section className="card">
          <div className={`banner ${labResult.ok ? 'ok' : 'bad'}`}>
            Sandbox run complete. Writes performed: {String(labResult.writesPerformed)} · Outbound replies captured: {String(labResult.outboundMessageSent)}
            {labResult.externalAiMayBeUsed ? ' · External AI was explicitly enabled for this run.' : ' · Rules-only local interpretation.'}
          </div>
          <h2>Transcript</h2>
          <table>
            <thead><tr><th>#</th><th>Channel</th><th>Student</th><th>Intent</th><th>Worker reply</th><th>Writes</th></tr></thead>
            <tbody>{labResult.transcript?.map((turn: any) => (
              <tr key={turn.turn}>
                <td>{turn.turn}</td>
                <td>{turn.channel}</td>
                <td>{turn.student}</td>
                <td><code>{turn.intent}</code></td>
                <td>{turn.reply}</td>
                <td><pre className="mono">{JSON.stringify(turn.writes, null, 2)}</pre></td>
              </tr>
            ))}</tbody>
          </table>
          <div className="grid">
            <div>
              <h2>Task states</h2>
              <pre className="mono">{JSON.stringify(labResult.taskStates, null, 2)}</pre>
            </div>
            <div>
              <h2>Final context</h2>
              <pre className="mono">{JSON.stringify(labResult.finalContext, null, 2)}</pre>
            </div>
            <div>
              <h2>Captured messages</h2>
              <pre className="mono">{JSON.stringify(labResult.capturedMessages, null, 2)}</pre>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
